'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';

interface PDFPageInfo {
  index: number;
  label: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function PageReorderTool() {
  const [file, setFile] = useState<File | null>(null);
  const [pages, setPages] = useState<PDFPageInfo[]>([]);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPDF = useCallback(async (f: File) => {
    if (f.type !== 'application/pdf') { alert('Please upload a PDF file.'); return; }
    setLoading(true);
    setFile(f);
    try {
      const { PDFDocument } = await import('pdf-lib');
      const arrayBuffer = await f.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      const count = pdf.getPageCount();
      setPages(Array.from({ length: count }, (_, i) => ({ index: i, label: `Page ${i + 1}` })));
    } catch (err) {
      console.error(err);
      alert('Failed to load PDF. Please try a different file.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) loadPDF(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) loadPDF(f);
  };

  const movePage = (index: number, dir: 'up' | 'down') => {
    const arr = [...pages];
    const target = dir === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setPages(arr);
  };

  const handleSave = async () => {
    if (!file || pages.length === 0) return;
    setProcessing(true);
    try {
      const { PDFDocument } = await import('pdf-lib');
      const arrayBuffer = await file.arrayBuffer();
      const srcPdf = await PDFDocument.load(arrayBuffer);
      const newPdf = await PDFDocument.create();
      const order = pages.map((p) => p.index);
      const copiedPages = await newPdf.copyPages(srcPdf, order);
      copiedPages.forEach((page) => newPdf.addPage(page));
      const pdfBytes = await newPdf.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reordered_${file.name}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Failed to reorder PDF. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-5">
      {!file ? (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload PDF</p>
          <p className="text-text-muted text-sm">Drag & drop or click to browse</p>
          <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={handleFileChange} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-surface rounded-xl p-3">
            <div>
              <p className="font-semibold text-text-primary text-sm">{file.name}</p>
              <p className="text-text-muted text-xs">{formatBytes(file.size)} · {pages.length} pages</p>
            </div>
            <button onClick={() => { setFile(null); setPages([]); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-text-muted hover:text-red-500 transition-colors text-xs">Remove</button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-text-muted text-sm">Loading pages…</div>
          ) : (
            <>
              <p className="text-sm text-text-secondary">Drag pages to reorder, or use the arrows:</p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {pages.map((page, i) => (
                  <div key={`${page.index}-${i}`} className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3">
                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-500 text-xs font-bold">{i + 1}</span>
                    </div>
                    <p className="flex-1 text-sm text-text-primary">{page.label}</p>
                    <div className="flex gap-1">
                      <button onClick={() => movePage(i, 'up')} disabled={i === 0} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors">
                        <svg className="w-3.5 h-3.5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" /></svg>
                      </button>
                      <button onClick={() => movePage(i, 'down')} disabled={i === pages.length - 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors">
                        <svg className="w-3.5 h-3.5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleSave}
                disabled={processing}
                className="btn-primary w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {processing ? (
                  <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Saving…</>
                ) : (
                  'Save Reordered PDF'
                )}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
